import {Network} from "../../../../network/Network";
import {Resources} from "../../../../resources/Resources";
import {ProductType} from "../../../../models/product/ProductType";
import {INetworkComponent} from "../../../../network/types/INetworkComponent";
import {HttpStatus} from "../../../../network/status/HttpStatus";
import {WebSocketCommunityEventName} from "../../../../network/socket/names/WebSocketCommunityEventName";
import {ICommunityAgentConversationModel} from "../../../../models/community/agent/ICommunityAgentConversationModel";
import {ICommunityAgentConversationSummaryModel} from "../../../../models/community/agent/ICommunityAgentConversationSummaryModel";
import {
    ICommunityAgentEndModel,
    ICommunityAgentErrorModel,
    ICommunityAgentStreamModel,
    ICommunityAgentNotificationModel,
    ICommunityAgentReadingModel,
    ICommunityAgentToolEventModel,
    ICommunityAgentTypingModel,
    ICommunityAgentUpdatedModel,
    ICommunityAgentWidgetEventModel
} from "../../../../models/community/agent/ICommunityAgentEventModels";
import {ICommunityAgentOnboardingModel} from "../../../../models/community/agent/ICommunityAgentOnboardingModel";
import {ICommunityAgentMessagesModel} from "../../../../models/community/agent/ICommunityAgentMessagesModel";
import {ICommunityAgentWidgetAnswerModel} from "../../../../models/community/agent/ICommunityAgentWidgetModel";
import {ICommunityChatAttachmentOutgoing} from "../../../../models/community/chat/ICommunityChatAttachmentOutgoing";
import {arrayRemove} from "../../../../sedestral/framework/utilities/ArrayRemove";

export interface ICommunityAgentChatListeners {
    ackEvents: (() => void)[];
    streamEvents: ((event: ICommunityAgentStreamModel) => void)[];
    toolStartEvents: ((event: ICommunityAgentToolEventModel) => void)[];
    toolEndEvents: ((event: ICommunityAgentToolEventModel) => void)[];
    readingEvents: ((event: ICommunityAgentReadingModel) => void)[];
    endEvents: ((event: ICommunityAgentEndModel) => void)[];
    errorEvents: ((event: ICommunityAgentErrorModel) => void)[];
    widgetEvents: ((event: ICommunityAgentWidgetEventModel) => void)[];
    notificationEvents: ((event: ICommunityAgentNotificationModel) => void)[];
    typingEvents: ((event: ICommunityAgentTypingModel) => void)[];
}

export interface ICommunityAgentChatResult {
    askId?: string;
    status?: number;
    // refus lisible du serveur : BUSY (un tour tourne déjà dans la conversation) ou RATE_LIMITED
    // (trop de messages en peu de temps, message = secondes avant de pouvoir réécrire)
    errorCode?: string;
    message?: string;
}

export class CommunityAgentChatService {

    private static listenersByConversationId: Map<string, ICommunityAgentChatListeners> = new Map();
    // une notification reçue panneau fermé n'a aucun listener par conversation : ce registre
    // global reçoit TOUTES les notifications, en plus du routage ciblé
    private static globalNotificationEvents: ((event: ICommunityAgentNotificationModel) => void)[] = [];
    private static globalUpdatedEvents: ((event: ICommunityAgentUpdatedModel) => void)[] = [];

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_ACK,
            (event: ICommunityAgentEndModel) => {
                this.listenersFor(event.conversationId)?.ackEvents.forEach(callback => callback());
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_STREAM,
            (event: ICommunityAgentStreamModel) => {
                this.listenersFor(event.conversationId)?.streamEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_TOOL_START,
            (event: ICommunityAgentToolEventModel) => {
                this.listenersFor(event.conversationId)?.toolStartEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_TOOL_END,
            (event: ICommunityAgentToolEventModel) => {
                this.listenersFor(event.conversationId)?.toolEndEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_READING,
            (event: ICommunityAgentReadingModel) => {
                this.listenersFor(event.conversationId)?.readingEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_END,
            (event: ICommunityAgentEndModel) => {
                this.listenersFor(event.conversationId)?.endEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_ERROR,
            (event: ICommunityAgentErrorModel) => {
                this.listenersFor(event.conversationId)?.errorEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_WIDGET,
            (event: ICommunityAgentWidgetEventModel) => {
                this.listenersFor(event.conversationId)?.widgetEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_NOTIFICATION,
            (event: ICommunityAgentNotificationModel) => {
                this.globalNotificationEvents.forEach(callback => callback(event));
                this.listenersFor(event.conversationId)?.notificationEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_UPDATED,
            (event: ICommunityAgentUpdatedModel) => {
                this.globalUpdatedEvents.forEach(callback => callback(event));
            });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_AGENT_CHAT_TYPING,
            (event: ICommunityAgentTypingModel) => {
                this.listenersFor(event.conversationId)?.typingEvents.forEach(callback => callback(event));
            });
    }

    /**
     * Le client n'a qu'un fil par communauté, quel que soit l'écran d'où il l'ouvre : l'ouverture
     * n'a donc rien à dire du contexte, seuls les messages le portent.
     */
    public static async open(communityId: string, conversationId?: string,
                             component?: INetworkComponent, peek?: boolean): Promise<ICommunityAgentConversationModel> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat/open`, {
            communityId: communityId,
            conversationId: conversationId,
            peek: peek
        }, component);
        return request?.status == HttpStatus.OK ? request.data : undefined;
    }

    public static async conversations(communityId: string,
                                      component?: INetworkComponent): Promise<ICommunityAgentConversationSummaryModel[]> {
        let request = await Network.get(ProductType.COMMUNITY,
            `/community/agent/chat/conversations?communityId=${communityId}`, component);
        return request?.status == HttpStatus.OK ? request.data : undefined;
    }

    public static async deleteConversation(conversationId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.delete(ProductType.COMMUNITY, `/community/agent/chat/${conversationId}`, component);
        return request?.status == HttpStatus.OK;
    }

    public static async renameConversation(conversationId: string, title: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/agent/chat/${conversationId}/title`, {title: title}, component);
        return request?.status == HttpStatus.OK;
    }

    public static async pinConversation(conversationId: string, pinned: boolean, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/agent/chat/${conversationId}/pin`, {pinned: pinned}, component);
        return request?.status == HttpStatus.OK;
    }

    public static async markSeen(conversationId: string): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat/${conversationId}/seen`, {});
        return request?.status == HttpStatus.OK;
    }

    /**
     * fresh : toujours une conversation neuve, jamais une conversation vide reprise ailleurs (un
     * autre onglet a pu en ouvrir une au même moment). Le refus du serveur remonte avec son code.
     */
    public static async createConversation(communityId: string, component?: INetworkComponent,
                                           fresh?: boolean): Promise<{ conversation?: ICommunityAgentConversationModel, errorCode?: string }> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat/conversations`, {
            communityId: communityId,
            fresh: fresh
        }, component);
        return request?.status == HttpStatus.OK
            ? {conversation: request.data}
            : {errorCode: request?.data?.errorCode};
    }

    /**
     * Le statut remonte avec l'askId : un refus pour tour déjà en cours ou limite journalière (429)
     * n'est pas une panne, et le panneau doit le dire autrement qu'en proposant de réessayer.
     * silent : le message déclenche l'agent sans jamais s'afficher, ni maintenant ni au
     * rechargement du fil. C'est un clic sur une étape du rail, pas une phrase du client.
     */
    public static async chat(conversationId: string, message?: string,
                             widgetAnswer?: ICommunityAgentWidgetAnswerModel,
                             component?: INetworkComponent,
                             context?: number, contextId?: string,
                             silent?: boolean,
                             attachments?: ICommunityChatAttachmentOutgoing[]): Promise<ICommunityAgentChatResult> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat`, {
            conversationId: conversationId,
            message: message,
            widgetAnswer: widgetAnswer,
            context: context,
            contextId: contextId,
            silent: silent,
            attachments: attachments,
            language: Resources.language
        }, component);
        return this.chatResult(request);
    }

    /**
     * Relance le tour qui vient d'échouer : ni message ni réponse de carte, le serveur repart de
     * l'historique. Sans ce chemin, « Réessayer » aurait obligé le client à réécrire sa demande —
     * et un clic sur une carte, lui, n'aurait rien eu à réécrire du tout.
     */
    public static async retry(conversationId: string, component?: INetworkComponent,
                              context?: number, contextId?: string): Promise<ICommunityAgentChatResult> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat`, {
            conversationId: conversationId,
            retry: true,
            context: context,
            contextId: contextId,
            language: Resources.language
        }, component);
        return this.chatResult(request);
    }

    private static chatResult(request: any): ICommunityAgentChatResult {
        if (request?.status == HttpStatus.OK) {
            return {askId: request.data, status: request.status};
        }
        return {
            status: request?.status,
            errorCode: request?.data?.errorCode,
            message: request?.data?.message
        };
    }

    public static async startBrand(communityId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat/start/brand`, {
            communityId: communityId
        }, component);
        return request?.status == HttpStatus.OK && request.data == true;
    }

    public static async stop(conversationId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/agent/chat/stop`, {
            conversationId: conversationId
        }, component);
        return request?.status == HttpStatus.OK && request.data == true;
    }

    public static async history(conversationId: string,
                                component?: INetworkComponent): Promise<ICommunityAgentConversationModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/agent/chat/${conversationId}`, component);
        return request?.data;
    }

    /**
     * page de messages strictement antérieurs au curseur, du plus ancien au plus récent : sert à
     * remonter le fil au-delà de la fenêtre chargée à l'ouverture
     */
    public static async messagesBefore(conversationId: string, before: number,
                                       component?: INetworkComponent): Promise<ICommunityAgentMessagesModel> {
        let request = await Network.get(ProductType.COMMUNITY,
            `/community/agent/chat/${conversationId}/messages?before=${before}`, component);
        // une réponse d'erreur arrive résolue avec un corps ApiError truthy : sans ce filtre, le
        // fil la prendrait pour une page et lirait un messages inexistant
        return request?.status == HttpStatus.OK ? request.data : undefined;
    }

    public static async onboarding(conversationId: string,
                                   component?: INetworkComponent): Promise<ICommunityAgentOnboardingModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/agent/chat/${conversationId}/onboarding`, component);
        return request?.data;
    }

    public static listen(conversationId: string,
                         callbacks: Partial<{
                             onAck: () => void,
                             onStream: (event: ICommunityAgentStreamModel) => void,
                             onToolStart: (event: ICommunityAgentToolEventModel) => void,
                             onToolEnd: (event: ICommunityAgentToolEventModel) => void,
                             onReading: (event: ICommunityAgentReadingModel) => void,
                             onEnd: (event: ICommunityAgentEndModel) => void,
                             onError: (event: ICommunityAgentErrorModel) => void,
                             onWidget: (event: ICommunityAgentWidgetEventModel) => void,
                             onNotification: (event: ICommunityAgentNotificationModel) => void,
                             onTyping: (event: ICommunityAgentTypingModel) => void
                         }>): () => void {

        let listeners = this.listenersByConversationId.get(conversationId);
        if (!listeners) {
            listeners = {
                ackEvents: [],
                streamEvents: [],
                toolStartEvents: [],
                toolEndEvents: [],
                readingEvents: [],
                endEvents: [],
                errorEvents: [],
                widgetEvents: [],
                notificationEvents: [],
                typingEvents: []
            };
            this.listenersByConversationId.set(conversationId, listeners);
        }

        if (callbacks.onAck) listeners.ackEvents.push(callbacks.onAck);
        if (callbacks.onStream) listeners.streamEvents.push(callbacks.onStream);
        if (callbacks.onToolStart) listeners.toolStartEvents.push(callbacks.onToolStart);
        if (callbacks.onToolEnd) listeners.toolEndEvents.push(callbacks.onToolEnd);
        if (callbacks.onReading) listeners.readingEvents.push(callbacks.onReading);
        if (callbacks.onEnd) listeners.endEvents.push(callbacks.onEnd);
        if (callbacks.onError) listeners.errorEvents.push(callbacks.onError);
        if (callbacks.onWidget) listeners.widgetEvents.push(callbacks.onWidget);
        if (callbacks.onNotification) listeners.notificationEvents.push(callbacks.onNotification);
        if (callbacks.onTyping) listeners.typingEvents.push(callbacks.onTyping);

        return () => {
            const current = this.listenersByConversationId.get(conversationId);
            if (current) {
                if (callbacks.onAck) arrayRemove(current.ackEvents, callbacks.onAck);
                if (callbacks.onStream) arrayRemove(current.streamEvents, callbacks.onStream);
                if (callbacks.onToolStart) arrayRemove(current.toolStartEvents, callbacks.onToolStart);
                if (callbacks.onToolEnd) arrayRemove(current.toolEndEvents, callbacks.onToolEnd);
                if (callbacks.onReading) arrayRemove(current.readingEvents, callbacks.onReading);
                if (callbacks.onEnd) arrayRemove(current.endEvents, callbacks.onEnd);
                if (callbacks.onError) arrayRemove(current.errorEvents, callbacks.onError);
                if (callbacks.onWidget) arrayRemove(current.widgetEvents, callbacks.onWidget);
                if (callbacks.onNotification) arrayRemove(current.notificationEvents, callbacks.onNotification);
                if (callbacks.onTyping) arrayRemove(current.typingEvents, callbacks.onTyping);

                if (current.ackEvents.length === 0 && current.streamEvents.length === 0
                    && current.toolStartEvents.length === 0 && current.toolEndEvents.length === 0
                    && current.readingEvents.length === 0
                    && current.endEvents.length === 0 && current.errorEvents.length === 0
                    && current.widgetEvents.length === 0
                    && current.notificationEvents.length === 0
                    && current.typingEvents.length === 0) {
                    this.listenersByConversationId.delete(conversationId);
                }
            }
        };
    }

    public static listenGlobal(callback: (event: ICommunityAgentNotificationModel) => void): () => void {
        this.globalNotificationEvents.push(callback);
        return () => arrayRemove(this.globalNotificationEvents, callback);
    }

    public static listenUpdated(callback: (event: ICommunityAgentUpdatedModel) => void): () => void {
        this.globalUpdatedEvents.push(callback);
        return () => arrayRemove(this.globalUpdatedEvents, callback);
    }

    private static listenersFor(conversationId: string): ICommunityAgentChatListeners | undefined {
        return this.listenersByConversationId.get(conversationId);
    }
}
